import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import AppShell from "../../components/layout/AppShell";
import ScopedOnly from "../../components/layout/ScopedOnly";

import ManageRoles from "../role/index";
import Industry from "../industry/index";
import DomainSkill from "../domain-skill/index";
import SoftSkill from "../soft-skill/index";
import Location from "../location";
import CreateJob from "./create-job-redesign";
import GenerateJd from "./generate-jd";
import CareerPages from "./career-pages";
import CodingQuestionEditor from "./coding-questions";
import ApprovalChains from "./approval-chains";
import MyApprovals from "./approvals";
import DefineInterview from "./screening";
import AddQuestions from "../add-questions";
import CreateJobDetails from "../job-details/index";
import InviteLink from "../invite-link";
import ValidityModal from "../invite-link/validityModal";
import InviteCandidates from "./invite-individually";
import MappingFields from "../invite-candidates/mapping-fields";
import FeatureTree from "../feature-tree/index";
import PermissionsManager from "../permissions-manager";
import InvitedCandidates from "./track-invites";
import LiveInterviews from "./live-interviews/live-interviews";
import TermsOfUse from "../user-signup-signin/terms-of-use";
import InterviewResponses from "../interview-responses";
import InterviewResponseRecruiter from "../interview-responses-recruiter";
import SummaryScores from "../summary-scores/index";
import UserManagement from "./team-members";
import SearchCandidates from "../search-candidates";
import SearchInterviews from "../search-interviews";
import MyAccount from "../my-account";
import Preferences from "../preferences";
import HelpSupport from "../help-support";
import Assistant from "./assistant/assistant";
import UploadResumes from "./upload-resumes/upload-resumes";
import Interviews from "./interviews-redesign";
import DashboardHome from "./dashboard-home/dashboard-home";
import DashboardV2 from "./dashboard-v2/dashboard-v2";
import Reports from "./reports/reports";
import Jobs from "./jobs/jobs";
import Pipeline from "./pipeline/pipeline";
import QuestionBank from "./questions-redesign";
import Audit from "./audit/audit";
import Cohorts from "./cohorts/cohorts";
import Exports from "./exports/exports";
import EmailTemplates from "./email-templates/email-templates";
import EmailSettings from "./email-settings/email-settings";
import EmailInbox from "./email-inbox/email-inbox";
import EmailDetail from "./email-inbox/email-detail";
import ComposeEmail from "./email-inbox/compose-email";
import WhatsAppSettings from "./whatsapp-settings/whatsapp-settings";
import WhatsAppInbox from "./whatsapp-inbox/whatsapp-inbox";
import Communications from "./communications/communications";
import Scheduling from "./scheduling/scheduling";
import Settings from "./settings/settings";
import CandidateExperienceSettings from "./settings/candidate-experience-settings";
import Compare from "./compare/compare";
import Shortlisted from "./hire/Shortlisted";
import Offered from "./hire/Offered";
import Hired from "./hire/Hired";
import CandidateReport from "./candidate-report-redesign/CandidateReport";
import InterviewReport from "./interview-report/InterviewReport";
import TalentPools from "./talent-pools/talent-pools";
import Integrations from "./integrations/integrations";
import SourceHub from "./source-hub/source-hub";
import AnalyticsHub from "./analytics/analytics";
import ScorecardSubmit from "./scorecard/scorecard-submit";
import Rubrics from "./rubrics/rubrics";
import RoundBuilder from "./round-builder/RoundBuilder";
import DataRequests from "./data-requests/data-requests";
import Applications from "./applications/applications";
import OfferBuilder from "./offers/offer-builder";
import OfferList from "./offers/offer-list";
import OfferDetail from "./offers/offer-detail";
import AssessmentList from "./assessments/assessment-list";
import AssessmentBuilder from "./assessments/assessment-builder";
import SchedulingCreate from "./scheduling-self/create-link";
import SchedulingBookings from "./scheduling-self/bookings-list";
import NotFound from "./not-found/NotFound";

// Redirect preserving the original query string (and optionally merging path params).
const KeepQueryRedirect = ({ to, extraParams }) => {
  const loc = useLocation();
  const params = useParams();
  const [basePath, baseQuery] = to.split("?");
  const merged = new URLSearchParams(baseQuery || "");
  for (const [k, v] of new URLSearchParams(loc.search).entries()) {
    if (!merged.has(k)) merged.set(k, v);
  }
  if (extraParams) {
    for (const [k, routeKey] of Object.entries(extraParams)) {
      if (params[routeKey] && !merged.has(k)) merged.set(k, params[routeKey]);
    }
  }
  const qs = merged.toString();
  return <Navigate to={qs ? `${basePath}?${qs}` : basePath} replace />;
};

const Admin = () => {
  const { userType } = useSelector((state) => state.signinSliceReducer);
  const isAdmin = userType === "admin";

  return (
    <Routes>
      <Route path="/terms-of-use/*" element={<TermsOfUse />} />

      {/* Legacy redirects (must come BEFORE catch-all wildcard route).
          KeepQueryRedirect preserves ?candidateId=... and similar from the old URL. */}
      <Route
        path="/candidate-video-answer-summary"
        element={<KeepQueryRedirect to="/admin/candidate-report" />}
      />
      <Route
        path="/candidate-video-answers"
        element={<KeepQueryRedirect to="/admin/interviews" />}
      />
      <Route
        path="/permissions-manager"
        element={<Navigate to="/admin/user-management" replace />}
      />
      <Route
        path="/shuffle-questions/:jobId"
        element={
          <KeepQueryRedirect
            to="/admin/question-bank"
            extraParams={{ jobId: "jobId" }}
          />
        }
      />
      <Route
        path="/interview-schedule"
        element={<KeepQueryRedirect to="/admin/scheduling" />}
      />
      <Route
        path="/invite-candidates"
        element={<KeepQueryRedirect to="/admin/talent-pools?action=invite" />}
      />
      <Route
        path="/cohorts"
        element={<KeepQueryRedirect to="/admin/talent-pools" />}
      />

      {/* Main app — wrapped in new AppShell */}
      <Route element={<AppShell />}>
        {/* Utility */}
        <Route path="/" element={<Navigate to="/admin/dashboard-v2" replace />} />
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/dashboard-v2" element={<DashboardV2 />} />
        <Route path="/assistant" element={<Assistant />} />
        <Route path="/reports" element={<Reports />} />
        {isAdmin && (
          <Route path="/user-management/*" element={<UserManagement />} />
        )}
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/candidate-experience" element={<CandidateExperienceSettings />} />

        {/* Plan stage */}
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/create-job/*" element={<CreateJob />} />
        <Route path="/generate-jd" element={<GenerateJd />} />
        <Route path="/career-pages/*" element={<CareerPages />} />
        <Route path="/coding-questions/:questionId" element={<CodingQuestionEditor />} />
        <Route path="/approval-chains/*" element={<ApprovalChains />} />
        <Route path="/approvals" element={<MyApprovals />} />
        <Route path="/question-bank" element={<QuestionBank />} />
        <Route path="/define-interview" element={<DefineInterview />} />

        {/* Source stage (scoped) */}
        <Route
          path="/upload-resumes"
          element={
            <ScopedOnly>
              <UploadResumes />
            </ScopedOnly>
          }
        />
        <Route
          path="/talent-pools"
          element={
            <ScopedOnly>
              <TalentPools />
            </ScopedOnly>
          }
        />
        <Route
          path="/source-hub"
          element={
            <ScopedOnly>
              <SourceHub />
            </ScopedOnly>
          }
        />
        <Route
          path="/invited-candidates"
          element={
            <ScopedOnly>
              <InvitedCandidates />
            </ScopedOnly>
          }
        />
        {/* Legacy invite page kept for deep links from older flows */}
        <Route
          path="/invite-candidates-legacy"
          element={
            <ScopedOnly>
              <InviteCandidates />
            </ScopedOnly>
          }
        />

        {/* Interview stage (scoped) */}
        <Route
          path="/interviews"
          element={
            <ScopedOnly>
              <Interviews />
            </ScopedOnly>
          }
        />
        <Route
          path="/live"
          element={
            <ScopedOnly>
              <LiveInterviews />
            </ScopedOnly>
          }
        />
        <Route
          path="/candidate-report"
          element={
            <ScopedOnly>
              <CandidateReport />
            </ScopedOnly>
          }
        />
        <Route path="/interview-report" element={<InterviewReport />} />
        <Route path="/rubrics" element={<Rubrics />} />
        <Route path="/jobs/:jobId/rounds" element={<RoundBuilder />} />
        <Route path="/round-builder" element={<RoundBuilder />} />
        <Route
          path="/scorecard/:candidateId/:jobId"
          element={<ScorecardSubmit />}
        />
        <Route
          path="/scorecard/:candidateId/:jobId/:roundId"
          element={<ScorecardSubmit />}
        />
        <Route path="/data-requests" element={<DataRequests />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/offers" element={<OfferList />} />
        <Route path="/offers/new" element={<OfferBuilder />} />
        <Route path="/offers/:id" element={<OfferDetail />} />
        <Route path="/assessments" element={<AssessmentList />} />
        <Route path="/assessments/new" element={<AssessmentBuilder />} />
        <Route path="/scheduling-create" element={<SchedulingCreate />} />
        <Route path="/scheduling-bookings" element={<SchedulingBookings />} />

        {/* Compare stage (scoped) */}
        <Route
          path="/compare"
          element={
            <ScopedOnly>
              <Compare />
            </ScopedOnly>
          }
        />

        {/* Hire stage (scoped) */}
        <Route
          path="/pipeline"
          element={
            <ScopedOnly>
              <Pipeline />
            </ScopedOnly>
          }
        />
        <Route
          path="/pipeline/:jobId"
          element={
            <ScopedOnly>
              <Pipeline />
            </ScopedOnly>
          }
        />
        <Route
          path="/shortlisted"
          element={
            <ScopedOnly>
              <Shortlisted />
            </ScopedOnly>
          }
        />
        <Route
          path="/offered"
          element={
            <ScopedOnly>
              <Offered />
            </ScopedOnly>
          }
        />
        <Route
          path="/hired"
          element={
            <ScopedOnly>
              <Hired />
            </ScopedOnly>
          }
        />

        {/* Settings sub-pages */}
        <Route path="/communications" element={<Communications />} />
        <Route path="/email-inbox" element={<EmailInbox />} />
        <Route path="/email-detail/:id" element={<EmailDetail />} />
        <Route path="/compose-email" element={<ComposeEmail />} />
        <Route path="/email-settings" element={<EmailSettings />} />
        <Route path="/whatsapp-inbox" element={<WhatsAppInbox />} />
        <Route path="/whatsapp-settings" element={<WhatsAppSettings />} />
        <Route path="/email-templates" element={<EmailTemplates />} />
        <Route path="/scheduling" element={<Scheduling />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/analytics" element={<AnalyticsHub />} />
        <Route path="/role/*" element={<ManageRoles />} />
        <Route path="/feature-tree" element={<PermissionsManager />} />
        {isAdmin && <Route path="/audit" element={<Audit />} />}

        {/* Misc / kept for compatibility with deep links */}
        <Route path="/industry/*" element={<Industry />} />
        <Route path="/domain-skill/*" element={<DomainSkill />} />
        <Route path="/soft-skill/*" element={<SoftSkill />} />
        <Route path="/location/*" element={<Location />} />
        <Route
          path="/job-details/job-position-details/*"
          element={<CreateJobDetails />}
        />
        <Route
          path="/scores-by-interview-round"
          element={<InterviewResponseRecruiter />}
        />
        {/* Legacy report routes now fold into /admin/candidate-report. */}
        <Route
          path="/_legacy-report"
          element={<KeepQueryRedirect to="/admin/candidate-report" />}
        />
        <Route
          path="/candidates-scores-summary"
          element={<KeepQueryRedirect to="/admin/candidate-report" />}
        />
        <Route path="/add-questions/*" element={<AddQuestions />} />
        <Route path="/test" element={<MappingFields />} />
        <Route path="/invite-link" element={<InviteLink />} />
        <Route path="/invite-modal" element={<ValidityModal />} />
        <Route path="/search-candidates" element={<SearchCandidates />} />
        <Route path="/search-interviews" element={<SearchInterviews />} />
        <Route path="/summary-by-position" element={<SummaryScores />} />
        <Route path="/my-account" element={<MyAccount />} />
        <Route path="/preferences" element={<Preferences />} />
        <Route path="/help-support" element={<HelpSupport />} />
        {/* /cohorts now redirects to /talent-pools (handled above) */}
        <Route path="/exports" element={<Exports />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default Admin;
